import React, { useContext, useEffect, useState } from "react";
import { Badge, Button, Dropdown, Menu, Typography } from "antd";
import { useHistory, withRouter } from "react-router";
import AccountService from "@Services/AccountService";
import SessionManager from "@Core/session";
import {
    FormatListBulleted,
    MenuOutlined,
    PermIdentity,
    CheckCircleOutlined,
    VerifiedUserOutlined,
    LockOutlined,
    BuildOutlined,
    LogoutOutlined,
} from "@mui/icons-material";
import { hasCapability } from "@Utils";
import { SystemContext } from "@Constants";
import { useQueryClient } from "@tanstack/react-query";
import appQueryClient from "@AppQueryClient";
import { MainContext } from "@Context/provider";
import { ViewMode } from "@Context/state";

export const LoginMenu: React.FC = () => {
    const { Text } = Typography;
    const { ui: uiState, reset: resetMainContext } = useContext(MainContext);
    const queryClient = useQueryClient();
    const history = useHistory();
    const [fullName, setFullName] = useState<string>(SessionManager.getServiceUser()?.fullName);

    const onSignOut = async () => {
        // reset context state
        resetMainContext();

        // log out
        const accountService = new AccountService();
        await accountService.logout();

        // Clear query clients and redirect to login
        queryClient.clear();
        appQueryClient.clear();
        history.push("/login");
    };

    const onNavigate = async (url: string) => {
        history.push(url);
    };

    // Check if on client as antd Menu triggers a UseLayoutEffect warning.
    const menu = (
        <div className={"login-menu"}>
            <Menu>
                <Menu.Item
                    icon={
                        <Badge>
                            <PermIdentity />
                        </Badge>
                    }
                    key={"login-menu-0"}
                    onClick={() => onNavigate("/")}
                >
                    {SessionManager.isAuthenticated ? "Profile" : null}
                </Menu.Item>
                <Menu.Divider key={"login-divider-1"} />
                <Menu.Item
                    icon={
                        <Badge>
                            <FormatListBulleted />
                        </Badge>
                    }
                    key={"login-menu-1"}
                    onClick={() => onNavigate("/account#details")}
                >
                    Account Details
                </Menu.Item>
                <Menu.Divider key={"login-divider-2"} />
                <Menu.Item
                    icon={
                        <Badge>
                            <CheckCircleOutlined />
                        </Badge>
                    }
                    key={"login-menu-2"}
                    onClick={() => onNavigate("/account#options")}
                >
                    Options
                </Menu.Item>
                <Menu.Divider key={"login-divider-3"} />
                <Menu.Item
                    icon={
                        <Badge>
                            <VerifiedUserOutlined />
                        </Badge>
                    }
                    key={"login-menu-3"}
                    onClick={() => onNavigate("/account#security")}
                >
                    Security
                </Menu.Item>
                <Menu.Divider key={"login-divider-4"} />
                <Menu.Item
                    icon={
                        <Badge>
                            <LockOutlined />
                        </Badge>
                    }
                    key={"login-menu-4"}
                    onClick={() => onNavigate("/account#privacy")}
                >
                    Privacy & Terms
                </Menu.Item>
                {hasCapability("Option/UpdateSystem", SystemContext) && (
                    <>
                        <Menu.Divider key={"login-divider-5"} />
                        <Menu.Item
                            icon={
                                <Badge>
                                    <BuildOutlined />
                                </Badge>
                            }
                            key={"login-menu-5"}
                            onClick={() => onNavigate("/account#system")}
                        >
                            System
                        </Menu.Item>
                    </>
                )}
                <Menu.Divider key={"login-divider-6"} />
                <Menu.Item
                    icon={
                        <Badge>
                            <LogoutOutlined />
                        </Badge>
                    }
                    key={"login-menu-6"}
                    onClick={() => onSignOut()}
                >
                    Sign out
                </Menu.Item>
            </Menu>
        </div>
    );

    useEffect(() => {
        setFullName(SessionManager.getServiceUser()?.fullName);
        // TODO: move into a variable when there is time to test
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [SessionManager.getServiceUser()?.fullName]);

    return (
        <Dropdown overlayClassName={"newUI-menu"} key={"login-menu-dropdown"} overlay={menu} trigger={["click"]}>
            <Button
                key={"login-dropdown"}
                className="login-menu-button"
                onClick={(e) => e.preventDefault()}
                type="text"
            >
                <MenuOutlined style={{ color: "white" }} />
                {uiState.mode === ViewMode.desktop ? (
                    <Text style={{ color: "white", fontSize: "15px" }}>
                        {SessionManager.isAuthenticated ? fullName : ""}
                    </Text>
                ) : null}
            </Button>
        </Dropdown>
    );
};
export default withRouter(LoginMenu);
